import styled from "@emotion/styled";

import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";

export enum AgentWorkEmptyStateKind {
  Projects = "projects",
  Tasks = "tasks",
}

const EMPTY_STATE_CONTENT = {
  [AgentWorkEmptyStateKind.Projects]: {
    icon: MDSIconKind.Folder,
    title: "Projects will appear as your work takes shape",
    description:
      "Talk to Mem about an outcome, or keep writing notes. Mem will group related tasks into projects and keep them current here.",
    messageTitle: "Message Mem",
    messageDescription: "Describe the outcome you're working toward.",
    notesTitle: "Keep writing notes",
    notesDescription: "Mem detects ongoing work across your notes.",
  },
  [AgentWorkEmptyStateKind.Tasks]: {
    icon: MDSIconKind.ListCheck,
    title: "Tasks will appear as Mem finds action items",
    description:
      "Tell Mem what you need to get done in a message, or keep writing notes. Mem will create and update tasks here as it recognizes next steps.",
    messageTitle: "Message Mem",
    messageDescription: "Say what needs to happen in your own words.",
    notesTitle: "Keep writing notes",
    notesDescription: "Mem detects actionable next steps in your notes.",
  },
} as const;

export const AgentWorkEmptyState = ({ kind }: { kind: AgentWorkEmptyStateKind }) => {
  const content = EMPTY_STATE_CONTENT[kind];

  return (
    <StyledEmptyState aria-label={`${kind} empty state`}>
      <StyledHeading>
        <StyledPrimaryIcon aria-hidden="true">
          <MDSIcon color={MDSIconColor.Secondary} kind={content.icon} size={22} />
        </StyledPrimaryIcon>
        <div>
          <MDSText as="h2" variant={MDSTextVariant.HeaderM}>
            {content.title}
          </MDSText>
          <StyledDescription color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphSm}>
            {content.description}
          </StyledDescription>
        </div>
      </StyledHeading>

      <StyledCreationPaths>
        <StyledCreationPath>
          <StyledPathIcon aria-hidden="true">
            <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.Message} size={16} />
          </StyledPathIcon>
          <div>
            <MDSText as="p" variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
              {content.messageTitle}
            </MDSText>
            <MDSText as="p" color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
              {content.messageDescription}
            </MDSText>
          </div>
        </StyledCreationPath>
        <StyledCreationPath>
          <StyledPathIcon aria-hidden="true">
            <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.Document} size={16} />
          </StyledPathIcon>
          <div>
            <MDSText as="p" variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
              {content.notesTitle}
            </MDSText>
            <MDSText as="p" color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
              {content.notesDescription}
            </MDSText>
          </div>
        </StyledCreationPath>
      </StyledCreationPaths>
    </StyledEmptyState>
  );
};

const StyledEmptyState = styled.section`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  min-height: 240px;
  padding: ${({ theme }) => theme.spacing[8]};
`;

const StyledHeading = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  max-width: 680px;
`;

const StyledPrimaryIcon = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.focused};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex: 0 0 auto;
  height: 44px;
  justify-content: center;
  width: 44px;
`;

const StyledDescription = styled(MDSText)`
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const StyledCreationPaths = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const StyledCreationPath = styled.div`
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.background.input.chrome.focused};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[5]};
`;

const StyledPathIcon = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  height: 20px;
  justify-content: center;
  width: 20px;
`;
